import {useState, useEffect, useMemo} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row, DatePicker, TimePicker, Select, Radio, AutoComplete, Spin, Transfer, Tag, message} from "antd";
import dayjs from "dayjs";
import {useSelector} from "react-redux";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {TextAreaField, SelectUserAll} from "~/components/Forms";
import {useCan} from "~/hooks";
import {currentUserSelector} from "~/features/Auth/authSlice";
import {groupApi, trainingScheduleApi} from "~/api";
import {TRAINING_TOPICS, ROOM_OPTIONS, DESIGNEE_GROUP_OPTIONS} from "../../constants";

const DISABLED_HOURS = () => [0, 1, 2, 3, 4, 5, 6, 23];

const TOPIC_OPTIONS = TRAINING_TOPICS.map(t => ({value: t}));

// Màu tag theo vai trò để phân biệt trưởng phòng / quản lý / nhóm trưởng
const ROLE_TAG_COLOR = {
	salesSupper:  'magenta',
	salesSenior:  'red',
	salesManager: 'volcano',
	salesLeader:  'gold',
	sales:        'default',
};

const TRAINING_TYPES = [
	{label: 'Toàn bộ phòng kinh doanh', value: 'all'},
	{label: 'Theo phòng', value: 'by_group'},
	{label: 'Theo cấp', value: 'by_level'},
	{label: 'Theo người chỉ định', value: 'by_designation'},
];

const LEVEL_OPTIONS = [
	{label: 'Trưởng phòng', value: 'truong_phong'},
	{label: 'Quản lý', value: 'quan_ly'},
	{label: 'Nhóm trưởng', value: 'nhom_truong'},
];

function TrainingScheduleFormAddEdit({item, onHandleSubmit}) {

	const currentUser      = useSelector(currentUserSelector);
	const canSelectTrainer = useCan('trainingScheduleSelectTrainer');
	const isEdit           = Boolean(item?.id);

	const [groups, setGroups]               = useState([]);
	const [dateOpen, setDateOpen]           = useState(false);
	const [startHourOpen, setStartHourOpen] = useState(false);
	const [endHourOpen, setEndHourOpen]     = useState(false);
	const [roster, setRoster]               = useState([]);
	const [loadingRoster, setLoadingRoster] = useState(false);
	const [designeeGroup, setDesigneeGroup] = useState(undefined);
	const [loadingGroup, setLoadingGroup]   = useState(false);

	useEffect(() => {
		groupApi.all().then(res => {
			if (res?.data) {
				setGroups(
					res.data
						.filter(g => g.totalUser > 0)
						.map(g => ({value: g.id, label: g.name}))
				);
			}
		});
	}, []);

	const trainerDefaultOptions = isEdit && item?.trainer
		? [{value: item.trainer.id, label: `${item.trainer.firstname} ${item.trainer.lastname}`}]
		: (currentUser && !canSelectTrainer
			? [{value: currentUser.id, label: `${currentUser.firstname} ${currentUser.lastname}`}]
			: []);

	const validationSchema = Yup.object().shape({
		topic:     Yup.string().required('Chủ đề đào tạo là bắt buộc'),
		room:      Yup.string().required('Phòng đào tạo là bắt buộc'),
		startDate: Yup.mixed().required('Ngày đào tạo là bắt buộc').nullable(),
		startHour: Yup.mixed().required('Giờ bắt đầu là bắt buộc').nullable(),
		endHour:   Yup.mixed()
			.required('Giờ kết thúc là bắt buộc')
			.nullable()
			.test('end-after-start', 'Giờ kết thúc phải sau giờ bắt đầu', function(value) {
				const {startHour} = this.parent;
				if (!startHour || !value) return true;
				const start = dayjs(startHour).hour() * 60 + dayjs(startHour).minute();
				const end   = dayjs(value).hour() * 60 + dayjs(value).minute();
				return end > start;
			})
			.test('max-4-hours', 'Thời gian đào tạo không được vượt quá 4 tiếng', function(value) {
				const {startHour} = this.parent;
				if (!startHour || !value) return true;
				const start = dayjs(startHour).hour() * 60 + dayjs(startHour).minute();
				const end   = dayjs(value).hour() * 60 + dayjs(value).minute();
				return (end - start) <= 4 * 60;
			}),
		trainerId: Yup.mixed().required('Người đào tạo là bắt buộc'),
		type:      Yup.string().required('Loại đào tạo là bắt buộc'),
		groupIds: Yup.array().when('type', {
			is:        'by_group',
			then:      (schema) => schema.min(1, 'Vui lòng chọn ít nhất một phòng'),
			otherwise: (schema) => schema.optional(),
		}),
		levels: Yup.array().when('type', {
			is:        'by_level',
			then:      (schema) => schema.min(1, 'Vui lòng chọn ít nhất một cấp'),
			otherwise: (schema) => schema.optional(),
		}),
		designeeIds: Yup.array().when('type', {
			is:        'by_designation',
			then:      (schema) => schema.min(1, 'Vui lòng chọn ít nhất một người'),
			otherwise: (schema) => schema.optional(),
		}),
	});

	const defaultValues = {
		topic:       '',
		content:     '',
		startDate:   null,
		startHour:   null,
		endHour:     null,
		trainerId:   canSelectTrainer ? undefined : currentUser?.id,
		type:        'all',
		room:        'tai_cho',
		groupIds:    [],
		levels:      [],
		designeeIds: [],
	};

	const {control, handleSubmit, formState: {isSubmitting, errors}, reset, watch, setValue} = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const watchType = watch('type');

	// Nạp danh sách nhân viên kinh doanh (1 lần) khi cần chọn người chỉ định
	useEffect(() => {
		if (watchType !== 'by_designation' || roster.length || loadingRoster) return;
		setLoadingRoster(true);
		trainingScheduleApi.salesUsers()
			.then(res => setRoster(res?.data || []))
			.catch(() => message.error('Không tải được danh sách nhân viên kinh doanh'))
			.finally(() => setLoadingRoster(false));
	}, [watchType]);

	// Nguồn dữ liệu cho Transfer: roster + những người đã chọn nhưng không còn trong roster (đã nghỉ)
	const designeeSource = useMemo(() => {
		const list = roster.map(u => ({...u, key: u.id, title: u.name}));
		if (isEdit && item?.designees?.length) {
			const seen = new Set(list.map(u => u.id));
			item.designees.forEach(u => {
				if (!seen.has(u.id)) {
					list.push({
						key:       u.id,
						id:        u.id,
						name:      `${u.firstname} ${u.lastname}`,
						title:     `${u.firstname} ${u.lastname}`,
						role:      u.role,
						roleLabel: 'Đã nghỉ',
						groupName: '',
					});
				}
			});
		}
		return list;
	}, [roster, item?.id]);

	useEffect(() => {
		if (isEdit && item?.id) {
			const startDayjs = item.startTime ? dayjs.unix(item.startTime) : null;
			const endDayjs   = item.endTime   ? dayjs.unix(item.endTime)   : null;
			reset({
				topic:       item.topic || '',
				content:     item.content || '',
				startDate:   startDayjs,
				startHour:   startDayjs,
				endHour:     endDayjs,
				trainerId:   item.trainerId || undefined,
				type:        item.type || 'all',
				room:        item.room || 'tai_cho',
				groupIds:    item.groupIds || [],
				levels:      item.levels || [],
				designeeIds: item.designeeIds || item.designees?.map(u => u.id) || [],
			});
			setDesigneeGroup(undefined);
		} else if (!isEdit) {
			reset({
				...defaultValues,
				trainerId: canSelectTrainer ? undefined : currentUser?.id,
			});
			setDesigneeGroup(undefined);
		}
	}, [item?.id]);

	const onSubmit = async (data) => {
		let startTimeUnix = null;
		let endTimeUnix   = null;

		if (data.startDate && data.startHour) {
			startTimeUnix = dayjs(data.startDate)
				.hour(dayjs(data.startHour).hour())
				.minute(dayjs(data.startHour).minute())
				.second(0)
				.unix();
		}
		if (data.startDate && data.endHour) {
			endTimeUnix = dayjs(data.startDate)
				.hour(dayjs(data.endHour).hour())
				.minute(dayjs(data.endHour).minute())
				.second(0)
				.unix();
		}

		const payload = {
			topic:       data.topic,
			content:     data.content,
			startTime:   startTimeUnix,
			endTime:     endTimeUnix,
			trainerId:   data.trainerId,
			type:        data.type,
			room:        data.room,
			groupIds:    data.type === 'by_group'       ? data.groupIds    : [],
			levels:      data.type === 'by_level'       ? data.levels      : [],
			designeeIds: data.type === 'by_designation' ? data.designeeIds : [],
		};
		await onHandleSubmit(payload, item);
	};

	const handleTypeChange = () => {
		setValue('groupIds', []);
		setValue('levels', []);
		setValue('designeeIds', []);
		setDesigneeGroup(undefined);
	};

	// Chọn nhóm nhân viên → lấy NV thỏa điều kiện, thay thế danh sách người được đào tạo
	const handleSelectDesigneeGroup = async (groupKey) => {
		setDesigneeGroup(groupKey);
		if (!groupKey) return;
		try {
			setLoadingGroup(true);
			const res   = await trainingScheduleApi.eligibleUsers(groupKey);
			const users = res?.data || [];
			setValue('designeeIds', users.map(u => u.id), {shouldValidate: true});
			if (!users.length) {
				message.info('Không có nhân viên thỏa điều kiện');
			} else {
				message.success(`Đã chọn ${users.length} nhân viên thỏa điều kiện`);
			}
		} catch (e) {
			message.error('Không tải được danh sách nhân viên');
		} finally {
			setLoadingGroup(false);
		}
	};

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading />}

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="topic" render={({field}) => (
						<div className="form-group">
							<label>Chủ đề đào tạo</label>
							<AutoComplete
								options={TOPIC_OPTIONS}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								filterOption={(input, option) =>
									(option?.value || '').toLowerCase().includes((input || '').toLowerCase())
								}
								placeholder="Chọn hoặc nhập chủ đề đào tạo"
								popupMatchSelectWidth={false}
								popupStyle={{zIndex: 10000}}
								className={errors.topic ? 'error' : ''}
								style={{width: '100%'}}
							/>
							{errors.topic && <p className="error-message">{errors.topic.message}</p>}
						</div>
					)} />
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={8}>
					<Controller control={control} name="startDate" render={({field}) => (
						<div className="form-group">
							<label>Ngày đào tạo</label>
							<DatePicker
								format="DD/MM/YYYY"
								value={field.value}
								open={dateOpen}
								onOpenChange={setDateOpen}
								onSelect={(date) => {
									field.onChange(date);
									setDateOpen(false);
								}}
								placeholder="Chọn ngày"
								popupStyle={{zIndex: 10000}}
								className={`form-control${errors.startDate ? ' error' : ''}`}
								style={{width: '100%'}}
							/>
							{errors.startDate && <p className="error-message">{errors.startDate.message}</p>}
						</div>
					)} />
				</Col>

				<Col span={8}>
					<Controller control={control} name="startHour" render={({field}) => (
						<div className="form-group">
							<label>Giờ bắt đầu</label>
							<TimePicker
								format="HH:mm"
								value={field.value}
								open={startHourOpen}
								onOpenChange={setStartHourOpen}
								onSelect={(time) => {
									field.onChange(time);
									setStartHourOpen(false);
								}}
								placeholder="Chọn giờ bắt đầu"
								popupStyle={{zIndex: 10000}}
								className={`form-control${errors.startHour ? ' error' : ''}`}
								style={{width: '100%'}}
								minuteStep={5}
								disabledTime={() => ({disabledHours: DISABLED_HOURS})}
								hideDisabledOptions
							/>
							{errors.startHour && <p className="error-message">{errors.startHour.message}</p>}
						</div>
					)} />
				</Col>

				<Col span={8}>
					<Controller control={control} name="endHour" render={({field}) => (
						<div className="form-group">
							<label>Giờ kết thúc</label>
							<TimePicker
								format="HH:mm"
								value={field.value}
								open={endHourOpen}
								onOpenChange={setEndHourOpen}
								onSelect={(time) => {
									field.onChange(time);
									setEndHourOpen(false);
								}}
								placeholder="Chọn giờ kết thúc"
								popupStyle={{zIndex: 10000}}
								className={`form-control${errors.endHour ? ' error' : ''}`}
								style={{width: '100%'}}
								minuteStep={5}
								disabledTime={() => ({disabledHours: DISABLED_HOURS})}
								hideDisabledOptions
							/>
							{errors.endHour && <p className="error-message">{errors.endHour.message}</p>}
						</div>
					)} />
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={24}>
					{canSelectTrainer ? (
						<Controller control={control} name="trainerId" render={({field}) => (
							<SelectUserAll
								label="Người đào tạo"
								options={trainerDefaultOptions}
								errors={errors}
								onlyActive={1}
								{...field}
							/>
						)} />
					) : (
						<div className="form-group">
							<label>Người đào tạo</label>
							<input
								className="form-control"
								value={`${currentUser?.firstname || ''} ${currentUser?.lastname || ''}`}
								disabled
								readOnly
							/>
						</div>
					)}
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="room" render={({field}) => (
						<div className="form-group">
							<label>Phòng đào tạo</label>
							<div style={{paddingTop: 6}}>
								<Radio.Group
									options={ROOM_OPTIONS}
									value={field.value}
									onChange={(e) => field.onChange(e.target.value)}
								/>
							</div>
							{errors.room && <p className="error-message">{errors.room.message}</p>}
						</div>
					)} />
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="type" render={({field}) => (
						<div className="form-group">
							<label>Đối tượng đào tạo</label>
							<div style={{paddingTop: 6}}>
								<Radio.Group
									options={TRAINING_TYPES}
									value={field.value}
									onChange={(e) => {
										field.onChange(e.target.value);
										handleTypeChange();
									}}
								/>
							</div>
							{errors.type && <p className="error-message">{errors.type.message}</p>}
						</div>
					)} />
				</Col>
			</Row>

			{watchType === 'by_group' && (
				<Row gutter={10}>
					<Col span={24}>
						<Controller control={control} name="groupIds" render={({field}) => (
							<div className="form-group">
								<label>Chọn phòng đào tạo</label>
								<Select
									mode="multiple"
									options={groups}
									value={field.value}
									onChange={field.onChange}
									placeholder="Chọn phòng..."
									style={{width: '100%'}}
									popupMatchSelectWidth={false}
									className={errors.groupIds ? 'error' : ''}
								/>
								{errors.groupIds && <p className="error-message">{errors.groupIds.message}</p>}
							</div>
						)} />
					</Col>
				</Row>
			)}

			{watchType === 'by_level' && (
				<Row gutter={10}>
					<Col span={24}>
						<Controller control={control} name="levels" render={({field}) => (
							<div className="form-group">
								<label>Chọn cấp đào tạo</label>
								<Select
									mode="multiple"
									options={LEVEL_OPTIONS}
									value={field.value}
									onChange={field.onChange}
									placeholder="Chọn cấp..."
									style={{width: '100%'}}
									className={errors.levels ? 'error' : ''}
								/>
								{errors.levels && <p className="error-message">{errors.levels.message}</p>}
							</div>
						)} />
					</Col>
				</Row>
			)}

			{watchType === 'by_designation' && (
				<>
					<Row gutter={10}>
						<Col span={24}>
							<div className="form-group">
								<label>Chọn nhanh theo nhóm nhân viên</label>
								<Select
									allowClear
									options={DESIGNEE_GROUP_OPTIONS}
									value={designeeGroup}
									onChange={handleSelectDesigneeGroup}
									placeholder="Chọn nhóm để tự thêm nhân viên thỏa điều kiện..."
									style={{width: '100%'}}
									popupMatchSelectWidth={false}
									popupStyle={{zIndex: 10000}}
									loading={loadingGroup}
									disabled={loadingGroup}
									notFoundContent={loadingGroup ? <Spin size="small" /> : null}
								/>
								<small className="text-muted">Chọn nhóm sẽ thay thế danh sách bên dưới; bạn vẫn có thể thêm/bớt thủ công.</small>
							</div>
						</Col>
					</Row>
					<Row gutter={10}>
						<Col span={24}>
							<Controller control={control} name="designeeIds" render={({field}) => (
								<div className="form-group">
									<label>Người được đào tạo {field.value?.length ? `(đã chọn ${field.value.length})` : ''}</label>
									<Transfer
										dataSource={designeeSource}
										targetKeys={field.value || []}
										onChange={(nextKeys) => field.onChange(nextKeys)}
										showSearch
										filterOption={(input, opt) => {
											const kw = (input || '').toLowerCase();
											return (opt.name || '').toLowerCase().includes(kw)
												|| (opt.roleLabel || '').toLowerCase().includes(kw)
												|| (opt.groupName || '').toLowerCase().includes(kw);
										}}
										render={(opt) => (
											<span style={{display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}>
												<span>{opt.name}</span>
												<Tag color={ROLE_TAG_COLOR[opt.role] || 'default'} style={{margin: 0, fontSize: 11, lineHeight: '16px'}}>
													{opt.roleLabel}
												</Tag>
												{opt.groupName && <small style={{color: '#999'}}>{opt.groupName}</small>}
											</span>
										)}
										titles={['Nhân viên kinh doanh', 'Được đào tạo']}
										listStyle={{flex: 1, height: 360}}
										locale={{
											itemUnit:          'người',
											itemsUnit:         'người',
											searchPlaceholder: 'Tìm theo tên / vai trò / phòng',
											notFoundContent:   loadingRoster ? <Spin size="small" /> : 'Không có nhân viên',
										}}
									/>
									{errors.designeeIds && <p className="error-message">{errors.designeeIds.message}</p>}
								</div>
							)} />
						</Col>
					</Row>
				</>
			)}

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="content" render={({field}) => (
						<TextAreaField label="Nội dung đào tạo" errors={errors} rows={4} {...field} />
					)} />
				</Col>
			</Row>

			<hr />
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">
					{isEdit ? 'Cập nhật' : 'Tạo'} lịch đào tạo
				</Button>
			</div>
		</form>
	);
}

export default TrainingScheduleFormAddEdit;
